import React from 'react';
import { Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useClientHeader } from './ClientHeaderContext';
import '../../ClientDetails.css';
import './AccountantCards.css';
import SummaryBar from '../../../Main/SummaryBar.jsx';

const AccountantCards = React.memo(({ clientsMode = false, showSubAccountantsCard = false }) => {
  const {
    t,
    direction,
    permissionsState,
    userTypeContext,
    beautifyNumber,
    clientsCount,
    clientsIcon,
    subAccountantsCount,
    subAccountantsIcon,
    unfinishedAssignments,
    viewAssignments,
    collectionBalance,
    viewCollectionPage,
    walletIcon,
    uploadIcon,
    totalUploads,
    openUploadsBreakdown,
    navigate,
    Constants,
    handleAddClick,
    cardType,
    filteredClients,
    isManageOnly,
  } = useClientHeader();

  const renderCountCard = (icon, label, value, accentLabel = false) => (
    <div className={`count-card${accentLabel ? ' count-card-accent' : ''}`}>

      {typeof icon === 'string'
        ? <img src={icon} alt="" className="count-card-icon" />
        : icon}
      <span className="count-card-label">{label}</span>
      <span className="count-card-value">{value}</span>
    </div>
  );

  const renderCard = (icon, label, value, onClick = null, tooltip = null) => {
    const iconNode =
      typeof icon === 'string'
        ? <img src={icon} alt="" className="metric-compact-icon-img" />
        : icon;

    const content = (
      <div
        className={`metric-compact${onClick ? ' metric-compact--clickable' : ''}`}
        onClick={onClick || undefined}
        onKeyDown={
          onClick
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        dir={direction}
      >
        <div className="metric-compact-text">
          <span className="metric-compact-label">{label}</span>
          <strong className="metric-compact-value">{value}</strong>
        </div>
        <span className="metric-compact-icon" aria-hidden="true">
          {iconNode}
        </span>
      </div>
    );

    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
  };

  if (clientsMode) {
    return (
      <div className={`main-cards ${direction}`}>
        <div className="main-cards-row">
          {renderCountCard(clientsIcon, t('clients'), beautifyNumber(clientsCount, 2, true), true)}
          {!permissionsState?.ViewSuAccountantClients &&
            renderCountCard(subAccountantsIcon, t('SubAccountants'), beautifyNumber(subAccountantsCount, 2, true))}
          <div className="main-cards-summary">
            <SummaryBar clients={filteredClients} t={t} variant="cards" />
          </div>
        </div>

        {!permissionsState?.CannotAddClient && (
          <div className="addClient-button" onClick={() => navigate('/AddClient')}>
            <AddCircleOutlineIcon className="addClient-button-icon" />
            <span>{t('addClient')}</span>
          </div>
        )}
      </div>
    );
  }

  // ========== SubAccountants Mode ==========
  if (cardType === 'subAccountants') {
    return (
      <div className={`main-cards ${direction}`}>
        <div style={{ display: 'flex', gap: '20px' }}>
          {renderCard(subAccountantsIcon, t('SubAccountants'), beautifyNumber(subAccountantsCount, 2, true))}
        </div>
        <div className="addClient-button" onClick={handleAddClick}>
          <span>{t('addSubAccountant')}</span>
        </div>
      </div>
    );
  }

  // ========== Accountant/Other Mode ==========
  const isNonClientUser = userTypeContext?.toString() !== Constants.UserType.Client;

  return (
    <div className="metric-compact-row">
      {isNonClientUser && !permissionsState?.ViewSubAccountantClients && (
        <>
          {renderCard(
            <AssignmentIcon className="metric-compact-icon-svg" />,
            t('UnfinishedAssignments'),
            unfinishedAssignments,
            () => viewAssignments('Assignments'),
            t('NavigateToAssignmentsPage')
          )}

          {renderCard(
            walletIcon,
            collectionBalance < 0 ? t('CollectionDebt') : t('Balance'),
            collectionBalance !== '-' ? beautifyNumber(collectionBalance) : '-',
            () => viewCollectionPage('CollectionPage'),
            t('NavigateToCollectionPage')
          )}
        </>
      )}

      {/* TotalUploads is a reporting metric — hidden for ManageOnly clients (archive/CRM-only view). */}
      {!isManageOnly && renderCard(
        uploadIcon,
        t('TotalUploads'),
        beautifyNumber(totalUploads, 2, true),
        openUploadsBreakdown || null,
        openUploadsBreakdown ? t('UploadsBreakdownTooltip') : null
      )}

      {showSubAccountantsCard && (
        <>
          <div style={{ display: 'flex', gap: '10px' }}>
            {renderCard(subAccountantsIcon, t('SubAccountants'), beautifyNumber(subAccountantsCount))}
          </div>
          <div className="addClient-button" onClick={handleAddClick}>
            <span>{t('addSubAccountant')}</span>
          </div>
        </>
      )}
    </div>
  );
});

export default AccountantCards;
